/* ==========================================================================
   Collection filter panel — behaviour
   --------------------------------------------------------------------------
   Presentation only. Filtering itself still goes through Shopify's native
   storefront filters and the existing .cf__form -> bt-collection.js AJAX
   pipeline; nothing here builds its own query string or fetches products.

   What it adds:
     * accessible accordion sections whose open/closed state survives a re-render
     * "See more" / "Show less" on long option lists
     * "Search filters" — narrows the OPTIONS, never the products
     * price: two number inputs and the slider kept in sync, clamped, debounced
     * "Show N products": scrolls to the results on desktop, applies on mobile
     * grid / list view toggle
     * mobile drawer: focus move + trap, Escape to close, focus returned

   bt-collection.js replaces the whole section on every filter change, so all
   DOM listeners are delegated from document and per-render state is re-applied
   by BSFilters.refresh(), which bt-collection.js calls after each swap.
   ========================================================================== */
(function () {
  'use strict';

  // The script tag lives in the layout, but jQuery's html() would re-run it if
  // it ever ended up inside a swapped section. Stay idempotent either way.
  if (window.BSFilters) {
    window.BSFilters.refresh();
    return;
  }

  var DESKTOP_MIN_WIDTH = 1200;
  var PRICE_DEBOUNCE_MS = 500;
  var SEARCH_DEBOUNCE_MS = 120;
  var STORE_SECTIONS = 'bsFilters:sections';
  var STORE_MORE = 'bsFilters:more';
  var STORE_SEARCH = 'bsFilters:search';
  var STORE_VIEW = 'bsFilters:view';
  var DRAWER_OPEN_CLASS = 'open-sidebar-canvas--sidebar-filter';

  // Unicode combining marks, built from char codes so the source file stays
  // pure ASCII (invisible combining characters in a literal class are a
  // liability in a repo that gets edited by several tools).
  var DIACRITICS = new RegExp(
    '[' + String.fromCharCode(0x300) + '-' + String.fromCharCode(0x36f) + ']',
    'g'
  );

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  /* ------------------------------------------------------------------ *
   * Pure helpers — no DOM. Exported on BSFilters.utils and unit tested
   * by tests/bs-collection-filters.test.mjs.
   * ------------------------------------------------------------------ */
  var utils = {
    /**
     * Fold case, accents and surrounding whitespace so "Brushed Gold" is found
     * by "gold", "GOLD" and "  góld ".
     */
    normalise: function (value) {
      return String(value == null ? '' : value)
        .normalize('NFD')
        .replace(DIACRITICS, '')
        .toLowerCase()
        .trim();
    },

    /** Does this option label match what was typed into "Search filters"? */
    matchesQuery: function (label, query) {
      var q = utils.normalise(query);
      if (q === '') return true;
      return utils.normalise(label).indexOf(q) !== -1;
    },

    /**
     * Clamp a typed price pair into the collection's real range and keep the
     * bounds ordered. An empty string means "no bound" and is preserved as
     * null so the parameter can be dropped rather than pinned to the extreme.
     */
    clampPriceRange: function (min, max, rangeMin, rangeMax) {
      var lo = Number(rangeMin) || 0;
      var hi = Number(rangeMax);
      if (!isFinite(hi)) hi = Infinity;

      var toBound = function (value) {
        if (value === '' || value === null || value === undefined) return null;
        var n = Number(value);
        if (!isFinite(n)) return null;
        return Math.min(Math.max(Math.round(n), lo), hi);
      };

      var lower = toBound(min);
      var upper = toBound(max);

      // min above max: the bound the shopper did not just leave wins, and the
      // simplest predictable rule is to pull the other one along.
      if (lower !== null && upper !== null && lower > upper) {
        upper = lower;
      }

      return { min: lower, max: upper };
    },

    /**
     * Count ACTIVE criteria the way the panel reports them: one per selected
     * list value, and exactly one for the whole price range no matter how many
     * bounds are set.
     *
     * @param {Array.<Array.<string>>} entries  [name, value] pairs, e.g. from FormData
     * @param {{min: number, max: number}} priceRange  the collection's full range
     */
    countActiveCriteria: function (entries, priceRange) {
      var range = priceRange || {};
      var rangeMin = Number(range.min) || 0;
      var rangeMax = Number(range.max);
      var total = 0;
      var priceActive = false;

      (entries || []).forEach(function (entry) {
        var name = entry[0];
        var value = entry[1];
        if (typeof name !== 'string' || name.indexOf('filter.') !== 0) return;
        if (value === '' || value === null || value === undefined) return;

        if (/\.price\.gte$/.test(name)) {
          if (Number(value) > rangeMin) priceActive = true;
          return;
        }
        if (/\.price\.lte$/.test(name)) {
          if (isFinite(rangeMax) && Number(value) < rangeMax) priceActive = true;
          return;
        }
        total += 1;
      });

      return priceActive ? total + 1 : total;
    },

    /**
     * Strip parameters that say nothing before a filter URL is built:
     *   * empty values
     *   * price bounds sitting on the ends of the collection's own range —
     *     the slider always writes both bounds into the inputs, so without
     *     this every URL would carry gte=0&lte=<max> and a shared link would
     *     look filtered when it is not
     *   * page, so any filter change returns to page 1
     *
     * Mutates and returns the URLSearchParams it is given.
     * Called from bt-collection.js when the filter form is submitted.
     */
    cleanFilterParams: function (params, priceRange) {
      var range = priceRange || {};
      var rangeMin = Number(range.min);
      var rangeMax = Number(range.max);
      var drop = [];

      params.forEach(function (value, name) {
        if (value === '') {
          drop.push(name);
          return;
        }
        if (/\.price\.gte$/.test(name) && isFinite(rangeMin) && Number(value) <= rangeMin) {
          drop.push(name);
        }
        if (/\.price\.lte$/.test(name) && isFinite(rangeMax) && Number(value) >= rangeMax) {
          drop.push(name);
        }
      });

      drop.forEach(function (name) {
        params.delete(name);
      });
      params.delete('page');
      return params;
    },

    /**
     * Drop ?page=N. Every filter change has to land the shopper back on page 1,
     * and Shopify's url_to_remove keeps whatever else was in the query string.
     */
    stripPageParam: function (url) {
      if (!url) return url;
      var stripped = String(url)
        .replace(/([?&])page=\d+&/, '$1')
        .replace(/[?&]page=\d+$/, '');
      return stripped;
    }
  };

  /* ------------------------------------------------------------------ *
   * Small DOM / storage helpers
   * ------------------------------------------------------------------ */
  function $all(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function isDesktop() {
    return window.innerWidth >= DESKTOP_MIN_WIDTH;
  }

  function readStore(key, fallback) {
    try {
      var raw = window.sessionStorage.getItem(key);
      return raw === null ? fallback : JSON.parse(raw);
    } catch (e) {
      return fallback;
    }
  }

  function writeStore(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* private mode / quota — state just stops persisting */
    }
  }

  function readView() {
    try {
      return window.localStorage.getItem(STORE_VIEW) || 'grid';
    } catch (e) {
      return 'grid';
    }
  }

  function writeView(value) {
    try {
      window.localStorage.setItem(STORE_VIEW, value);
    } catch (e) {
      /* ignore */
    }
  }

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      var args = arguments;
      var self = this;
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  function panelFor(toggle) {
    var id = toggle.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  /* ------------------------------------------------------------------ *
   * Accordion sections
   * ------------------------------------------------------------------ */
  function setSectionOpen(section, open, persist) {
    var toggle = section.querySelector('[data-bs-toggle]');
    var panel = toggle && panelFor(toggle);
    if (!toggle || !panel) return;

    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) {
      panel.removeAttribute('hidden');
    } else {
      panel.setAttribute('hidden', '');
    }

    if (persist) {
      var state = readStore(STORE_SECTIONS, {}) || {};
      state[section.getAttribute('data-bs-section-key')] = open;
      writeStore(STORE_SECTIONS, state);
    }
  }

  function restoreSections() {
    var state = readStore(STORE_SECTIONS, null);
    if (!state) return;
    $all('[data-bs-section]').forEach(function (section) {
      var key = section.getAttribute('data-bs-section-key');
      if (Object.prototype.hasOwnProperty.call(state, key)) {
        setSectionOpen(section, !!state[key], false);
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * "See more" / "Show less"
   * ------------------------------------------------------------------ */
  function setMoreExpanded(button, expanded, persist) {
    // The search template reuses the option markup but supplies its own <ul>
    // without data-bs-options, so fall back to the nearest list.
    var list = button.closest('[data-bs-options]') || button.closest('ul');
    if (!list) return;

    $all('[data-bs-overflow]', list).forEach(function (option) {
      if (expanded) {
        option.removeAttribute('hidden');
      } else {
        option.setAttribute('hidden', '');
      }
    });

    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    button.textContent = expanded
      ? button.getAttribute('data-bs-less-label')
      : button.getAttribute('data-bs-more-label');

    if (persist) {
      var section = button.closest('[data-bs-section]');
      var key = section && section.getAttribute('data-bs-section-key');
      if (!key) return;
      var state = readStore(STORE_MORE, {}) || {};
      state[key] = expanded;
      writeStore(STORE_MORE, state);
    }
  }

  function restoreMore() {
    var state = readStore(STORE_MORE, null);
    if (!state) return;
    $all('[data-bs-more]').forEach(function (button) {
      var section = button.closest('[data-bs-section]');
      var key = section && section.getAttribute('data-bs-section-key');
      if (key && state[key]) setMoreExpanded(button, true, false);
    });
  }

  /* ------------------------------------------------------------------ *
   * "Search filters" — narrows the options, never the products
   * ------------------------------------------------------------------ */
  function clearSearch() {
    $all('[data-bs-section]').forEach(function (section) {
      section.removeAttribute('hidden');
    });
    $all('[data-bs-option]').forEach(function (option) {
      if (option.hasAttribute('data-bs-overflow')) {
        option.setAttribute('hidden', '');
      } else {
        option.removeAttribute('hidden');
      }
    });
    $all('.bs-filters__more-row').forEach(function (row) {
      row.removeAttribute('hidden');
    });
    $all('[data-bs-more]').forEach(function (button) {
      setMoreExpanded(button, false, false);
    });

    restoreSections();
    restoreMore();

    var empty = document.querySelector('.bs-filters__search-empty');
    if (empty) empty.setAttribute('hidden', '');
  }

  function applySearch(query) {
    var q = utils.normalise(query);
    writeStore(STORE_SEARCH, q);

    if (q === '') {
      clearSearch();
      return;
    }

    var matches = 0;

    $all('[data-bs-section]').forEach(function (section) {
      var options = $all('[data-bs-option]', section);

      // Price and the category tree carry no matchable options.
      if (options.length === 0) {
        section.setAttribute('hidden', '');
        return;
      }

      var sectionMatches = 0;
      options.forEach(function (option) {
        var hit = utils.matchesQuery(option.getAttribute('data-bs-option-label'), q);
        if (hit) {
          option.removeAttribute('hidden');
          sectionMatches += 1;
        } else {
          option.setAttribute('hidden', '');
        }
      });

      // While searching, every hit is shown — "See more" would hide results.
      $all('.bs-filters__more-row', section).forEach(function (row) {
        row.setAttribute('hidden', '');
      });

      if (sectionMatches > 0) {
        section.removeAttribute('hidden');
        setSectionOpen(section, true, false);
      } else {
        section.setAttribute('hidden', '');
      }
      matches += sectionMatches;
    });

    var empty = document.querySelector('.bs-filters__search-empty');
    if (empty) {
      if (matches === 0) {
        empty.removeAttribute('hidden');
      } else {
        empty.setAttribute('hidden', '');
      }
    }
  }

  function restoreSearch() {
    var stored = readStore(STORE_SEARCH, '');
    var input = document.querySelector('[data-bs-filter-search]');
    if (!input) return;
    if (stored) {
      input.value = stored;
      applySearch(stored);
    }
  }

  /* ------------------------------------------------------------------ *
   * Price
   * ------------------------------------------------------------------ */
  function priceRangeOf(wrap) {
    return {
      min: Number(wrap.getAttribute('data-range-min')) || 0,
      max: Number(wrap.getAttribute('data-range-max'))
    };
  }

  function commitPrice(wrap, submit) {
    var minInput = wrap.querySelector('[data-bs-price-min]');
    var maxInput = wrap.querySelector('[data-bs-price-max]');
    if (!minInput || !maxInput) return;

    var range = priceRangeOf(wrap);
    var clamped = utils.clampPriceRange(minInput.value, maxInput.value, range.min, range.max);

    var nextMin = clamped.min === null ? '' : String(clamped.min);
    var nextMax = clamped.max === null ? '' : String(clamped.max);
    var changed = minInput.value !== nextMin || maxInput.value !== nextMax;

    minInput.value = nextMin;
    maxInput.value = nextMax;

    var slider = wrap.querySelector('.cf__price-slider');
    if (slider && slider.noUiSlider) {
      // set() fires 'update' (which mirrors back into these inputs) but not
      // 'change', so this cannot loop back into another submit.
      slider.noUiSlider.set([
        clamped.min === null ? range.min : clamped.min,
        clamped.max === null ? range.max : clamped.max
      ]);
    }

    syncTriggerCount();

    if (submit && isDesktop()) {
      var last = wrap.getAttribute('data-bs-last-commit');
      var current = nextMin + '|' + nextMax;
      if (last === current) return;
      wrap.setAttribute('data-bs-last-commit', current);
      submitFilterForm(wrap);
    } else if (changed) {
      wrap.setAttribute('data-bs-last-commit', nextMin + '|' + nextMax);
    }
  }

  var commitPriceDebounced = debounce(function (wrap) {
    commitPrice(wrap, true);
  }, PRICE_DEBOUNCE_MS);

  function submitFilterForm(node) {
    // Route through the theme's own handler, which fetches the filtered section
    // directly (it cannot use the form's submit event — an app on this store
    // stops that event from bubbling; see BTCollection.submitFilterForm).
    if (window.BTCollection && typeof window.BTCollection.submitFilterForm === 'function' && window.jQuery) {
      window.BTCollection.submitFilterForm(window.jQuery(node));
      return;
    }
    var form = node.closest ? node.closest('.cf__form') : null;
    if (!form) form = document.querySelector('.cf__form');
    if (!form) return;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.submit();
    }
  }

  /**
   * noUiSlider occasionally initialises with the wrong handle value when the
   * panel column has not settled its width yet (max lands mid-track instead of
   * at the range end). The intended bounds live on the slider's data-start /
   * data-end, so re-assert them once layout is stable. Idempotent: a correct
   * slider is left untouched, and set() does not fire noUiSlider's 'change', so
   * this never triggers a stray fetch.
   */
  function fixPriceSliders() {
    $all('.cf__price-slider').forEach(function (slider) {
      if (!slider.noUiSlider) return;
      var start = parseFloat(slider.getAttribute('data-start'));
      var end = parseFloat(slider.getAttribute('data-end'));
      if (isNaN(start) || isNaN(end)) return;
      var current = slider.noUiSlider.get();
      if (!Array.isArray(current)) return;
      var curMin = Math.round(parseFloat(current[0]));
      var curMax = Math.round(parseFloat(current[1]));
      if (curMin !== Math.round(start) || curMax !== Math.round(end)) {
        slider.noUiSlider.set([start, end]);
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Active-criteria count on the toolbar triggers
   * ------------------------------------------------------------------ */
  function syncTriggerCount() {
    var form = document.querySelector('.cf__form');
    var triggers = $all('.bs-toolbar__filters');
    if (!form || triggers.length === 0 || typeof window.FormData !== 'function') return;

    var entries = [];
    try {
      var data = new window.FormData(form);
      data.forEach(function (value, name) {
        entries.push([name, value]);
      });
    } catch (e) {
      return;
    }

    var wrap = document.querySelector('[data-bs-price]');
    var range = wrap ? priceRangeOf(wrap) : {};
    var count = utils.countActiveCriteria(entries, range);

    triggers.forEach(function (trigger) {
      var label = trigger.querySelector('span:not(.visually-hidden)');
      if (!label) return;
      var base = trigger.getAttribute('data-bs-base-label');
      if (base === null) {
        base = label.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
        trigger.setAttribute('data-bs-base-label', base);
      }
      label.textContent = count > 0 ? base + ' (' + count + ')' : base;
    });
  }

  /* ------------------------------------------------------------------ *
   * Grid / list view
   * ------------------------------------------------------------------ */
  function applyView(view) {
    var root = document.querySelector('.collection-template-section');
    if (root) {
      root.classList.toggle('bs-view--list', view === 'list');
      root.classList.toggle('bs-view--grid', view !== 'list');
    }
    $all('[data-bs-view]').forEach(function (button) {
      var isCurrent = button.getAttribute('data-bs-view') === view;
      button.classList.toggle('is-active', isCurrent);
      button.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
    });
  }

  /* ------------------------------------------------------------------ *
   * Mobile drawer — focus move, focus trap, Escape, focus return
   * ------------------------------------------------------------------ */
  var drawer = {
    isOpen: false,
    lastTrigger: null,

    node: function () {
      return document.getElementById('sidebar-filter');
    },

    open: function () {
      var node = this.node();
      if (!node || this.isOpen) return;
      this.isOpen = true;
      node.setAttribute('role', 'dialog');
      node.setAttribute('aria-modal', 'true');
      // Wait for the theme's slide animation before pulling focus in.
      window.setTimeout(function () {
        if (node.getAttribute('aria-modal') === 'true') node.focus();
      }, 150);
    },

    close: function () {
      var node = this.node();
      if (!node || !this.isOpen) return;
      this.isOpen = false;
      node.removeAttribute('role');
      node.removeAttribute('aria-modal');
      if (this.lastTrigger && document.contains(this.lastTrigger)) {
        this.lastTrigger.focus();
      } else {
        var fallback = document.querySelector('[data-bs-filter-open]');
        if (fallback) fallback.focus();
      }
      this.lastTrigger = null;
    },

    requestClose: function () {
      var button = document.querySelector('#sidebar-filter .bt-sidebar__close');
      if (button) {
        button.click();
      } else if (window.jQuery) {
        window.jQuery('.overlay').trigger('click');
      }
    },

    trap: function (event) {
      if (event.key !== 'Tab' || !this.isOpen) return;
      var node = this.node();
      if (!node) return;

      var focusable = $all(FOCUSABLE, node).filter(function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
      if (focusable.length === 0) {
        event.preventDefault();
        node.focus();
        return;
      }

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      // Focus drifted out of the drawer (browser chrome, an outside click):
      // pull it back rather than letting Tab walk the page behind the overlay.
      if (!node.contains(document.activeElement) && document.activeElement !== node) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && (document.activeElement === first || document.activeElement === node)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  function watchDrawer() {
    var sync = function () {
      var open = document.body.classList.contains(DRAWER_OPEN_CLASS);
      if (open) {
        drawer.open();
      } else {
        drawer.close();
      }
    };

    if (typeof window.MutationObserver === 'function') {
      new window.MutationObserver(sync).observe(document.body, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
    sync();
  }

  /* ------------------------------------------------------------------ *
   * Wiring
   * ------------------------------------------------------------------ */
  var searchDebounced = debounce(function (value) {
    applySearch(value);
  }, SEARCH_DEBOUNCE_MS);

  function onClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var toggle = target.closest('[data-bs-toggle]');
    if (toggle) {
      event.preventDefault();
      var section = toggle.closest('[data-bs-section]');
      setSectionOpen(section, toggle.getAttribute('aria-expanded') !== 'true', true);
      return;
    }

    var more = target.closest('[data-bs-more]');
    if (more) {
      event.preventDefault();
      setMoreExpanded(more, more.getAttribute('aria-expanded') !== 'true', true);
      return;
    }

    var viewButton = target.closest('[data-bs-view]');
    if (viewButton) {
      event.preventDefault();
      var view = viewButton.getAttribute('data-bs-view');
      writeView(view);
      applyView(view);
      return;
    }

    // The theme opens .bt-dropdown on hover only (click is wired for touch
    // devices alone), so keyboard users and anyone whose hover was lost can
    // never open the sort list. Enter/click on the trigger opens it; the
    // theme's own body-click and mouseleave handlers still close it.
    var sortTrigger = target.closest('.bs-toolbar__sort-trigger');
    if (sortTrigger && !(window.BT && window.BT.data && window.BT.data.isTouchDevide)) {
      event.preventDefault();
      var sortWrap = sortTrigger.closest('.bt-dropdown');
      if (sortWrap) sortWrap.classList.add('active');
      return;
    }

    var focusButton = target.closest('[data-bs-filter-focus]');
    if (focusButton) {
      event.preventDefault();
      var panel = document.getElementById('sidebar-filter');
      if (panel) {
        panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        panel.focus({ preventScroll: true });
      }
      return;
    }

    var openButton = target.closest('[data-bs-filter-open]');
    if (openButton) {
      drawer.lastTrigger = openButton;
      return; // the theme's bt-sidebar handler does the opening
    }

    var submit = target.closest('[data-bs-submit]');
    if (submit) {
      event.preventDefault();
      if (isDesktop()) {
        // Desktop already filtered on each tick, so just take the shopper to
        // the results.
        var results = document.querySelector('.collection__products');
        if (results) {
          results.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        // Mobile does not auto-apply — this button is what applies. A native
        // submit would be swallowed, so drive the fetch directly, then close.
        submitFilterForm(submit);
        drawer.requestClose();
      }
      return;
    }

    // "Clear all" also clears the stored option-search term. Page-param
    // stripping for filter links is handled in bt-collection.js (scoped to the
    // panel) so it never touches pagination links, which are also .cf__link.
    var clearAll = target.closest('[data-bs-clear-all]');
    if (clearAll) {
      writeStore(STORE_SEARCH, '');
    }
  }

  function onInput(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    if (target.matches('[data-bs-filter-search]')) {
      searchDebounced(target.value);
      return;
    }

    var priceWrap = target.closest('[data-bs-price]');
    if (priceWrap && target.matches('[data-bs-price-min],[data-bs-price-max]')) {
      commitPriceDebounced(priceWrap);
    }
  }

  function onChange(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var priceWrap = target.closest('[data-bs-price]');
    if (priceWrap && target.matches('[data-bs-price-min],[data-bs-price-max]')) {
      commitPrice(priceWrap, true);
      return;
    }

    if (target.matches('.bs-filters__input')) {
      syncTriggerCount();
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape' && drawer.isOpen) {
      event.preventDefault();
      drawer.requestClose();
      return;
    }

    if (event.key === 'Tab' && drawer.isOpen) {
      drawer.trap(event);
      return;
    }

    var target = event.target;
    if (!target || !target.matches) return;
    // Escape closes an open sort list and hands focus back to its trigger.
    if (event.key === 'Escape') {
      var openSort = document.querySelector('.bs-toolbar__sort.active');
      if (openSort) {
        event.preventDefault();
        openSort.classList.remove('active');
        var trigger = openSort.querySelector('.bs-toolbar__sort-trigger');
        if (trigger) trigger.focus();
        return;
      }
    }

    // Enter inside "Search filters" must not submit the whole filter form.
    if (event.key === 'Enter' && target.matches('[data-bs-filter-search]')) {
      event.preventDefault();
      applySearch(target.value);
      return;
    }

    if (event.key === 'Enter' && target.matches('[data-bs-price-min],[data-bs-price-max]')) {
      event.preventDefault();
      var wrap = target.closest('[data-bs-price]');
      if (wrap) commitPrice(wrap, true);
    }
  }

  var BSFilters = {
    utils: utils,

    /** Re-apply everything that a section swap wipes out. */
    refresh: function () {
      restoreSections();
      restoreMore();
      restoreSearch();
      applyView(readView());
      syncTriggerCount();
      fixPriceSliders();
      if (document.body.classList.contains(DRAWER_OPEN_CLASS)) {
        drawer.isOpen = false;
        drawer.open();
      }
    },

    init: function () {
      document.addEventListener('click', onClick, true);
      document.addEventListener('input', onInput);
      document.addEventListener('change', onChange);
      document.addEventListener('keydown', onKeydown);
      watchDrawer();
      this.refresh();
      // The theme builds the sliders from its own section loader, which may run
      // after this script; re-assert their bounds once everything has settled.
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(fixPriceSliders);
      }
      window.addEventListener('load', fixPriceSliders);
    }
  };

  window.BSFilters = BSFilters;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      BSFilters.init();
    });
  } else {
    BSFilters.init();
  }
})();
